import { gql, useMutation } from '@apollo/client';
import { Box, Button, Flex, Icon, Modal } from '@energiebespaarders/symbols';
import { Book, Color, Small, Smaller } from '@energiebespaarders/symbols/helpers';
import { useIsMobile } from '@energiebespaarders/symbols/hooks';
import { ShieldCheck } from '@energiebespaarders/symbols/icons/line';
import { ThumbsUp } from '@energiebespaarders/symbols/icons/solid';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ConsentFragment } from '~/fragments/Customer';
import { useContactModal } from '~/hooks/useContactModal';
import useIsAuthenticated from '~/hooks/useIsAuthenticated';
import { useMeOptional } from '~/hooks/useMe';
import usePathname from '~/hooks/usePathname';
import type {
  me_me_Customer,
  me_me_Customer_consent,
  me_me_PartnerAgent,
  me_me_PartnerAgent_consent,
} from '~/types/generated/me';
import type {
  updateConsentCustomer,
  updateConsentCustomerVariables,
} from '~/types/generated/updateConsentCustomer';
import type {
  updateConsentPartnerAgent,
  updateConsentPartnerAgentVariables,
} from '~/types/generated/updateConsentPartnerAgent';
import {
  createPartnerTAndCConsent,
  createPrivacyConsent,
  createTAndCConsent,
} from '~/utils/createConsent';
import HybridLink from '../HybridLink';

export const UPDATE_CONSENT_CUSTOMER = gql`
  ${ConsentFragment}
  mutation updateConsentCustomer($userId: ID!, $customer: CustomerInput!) {
    updateCustomer(id: $userId, customer: $customer) {
      id
      firstName
      lastName
      email
      consent {
        ...Consent
      }
    }
  }
`;

export const UPDATE_CONSENT_LEAD = gql`
  ${ConsentFragment}
  mutation updateConsentLead($userId: ID!, $lead: LeadInput!) {
    updateLead(id: $userId, lead: $lead) {
      id
      consent {
        ...Consent
      }
    }
  }
`;

export const UPDATE_CONSENT_PARTNERAGENT = gql`
  ${ConsentFragment}
  mutation updateConsentPartnerAgent($userId: ID!, $partnerAgent: UpdatePartnerAgentInput!) {
    updatePartnerAgent(id: $userId, partnerAgent: $partnerAgent) {
      id
      firstName
      lastName
      email
      consent {
        ...Consent
      }
    }
  }
`;

const ConsentModal: React.FC = () => {
  const { me, setMe } = useMeOptional<me_me_Customer | me_me_PartnerAgent>();
  const mobile = useIsMobile();
  const [consentModalOpen, setConsentModalOpen] = useState(false);
  const isAuthenticated = useIsAuthenticated();
  const { setContactModalContent } = useContactModal();

  const pathname = usePathname();

  const userIsPartnerAgent = me?.__typename === 'PartnerAgent';
  const partnerAgentTermsConsent = createPartnerTAndCConsent('partnerAgentSignup');
  const partnerAgentPrivacyConsent = createPrivacyConsent('partnerAgentSignup');
  const isUserAuthenticated = isAuthenticated.asCustomer || isAuthenticated.asPartnerAgent;

  const checkHasRequiredConsent = useCallback(
    (storedConsent: readonly me_me_Customer_consent[] | me_me_PartnerAgent_consent[]) => {
      const requiredConsents = ['terms and conditions', 'privacy'];
      return requiredConsents.every(
        title => storedConsent?.some(c => c.title === title && !c.withdrawn),
      );
    },
    [],
  );

  const [updateCustomerConsent] = useMutation<
    updateConsentCustomer,
    updateConsentCustomerVariables
  >(UPDATE_CONSENT_CUSTOMER, {
    onCompleted: ({ updateCustomer }) => {
      if (updateCustomer?.consent) {
        setConsentModalOpen(!checkHasRequiredConsent([...updateCustomer.consent]));
        setMe({ ...me!, consent: updateCustomer.consent });
      }
    },
  });

  const [updatePartnerAgentConsent] = useMutation<
    updateConsentPartnerAgent,
    updateConsentPartnerAgentVariables
  >(UPDATE_CONSENT_PARTNERAGENT, {
    onCompleted: ({ updatePartnerAgent }) => {
      if (updatePartnerAgent?.consent) {
        setMe({ ...me!, consent: updatePartnerAgent.consent });
      }
      setConsentModalOpen(false);
    },
  });

  useEffect(() => {
    if (isUserAuthenticated && me?.consent)
      setConsentModalOpen(!checkHasRequiredConsent([...me.consent]));
  }, [me, checkHasRequiredConsent, isUserAuthenticated]);

  const handleStoreConsent = useCallback(() => {
    if (!isUserAuthenticated) {
      console.error('User is not authenticated');
      return;
    }

    if (me?.__typename === 'PartnerAgent') {
      void updatePartnerAgentConsent({
        variables: {
          userId: me.id,
          partnerAgent: { consent: [partnerAgentTermsConsent, partnerAgentPrivacyConsent] },
        },
      });
    }

    if (me?.__typename === 'Customer') {
      const consent = [
        ...me.consent,
        createTAndCConsent('consent-modal'),
        createPrivacyConsent('consent-modal'),
      ] as me_me_Customer_consent[];
      void updateCustomerConsent({ variables: { userId: me.id, customer: { consent } } });
    }
  }, [
    me?.__typename,
    me?.consent,
    me?.id,
    partnerAgentPrivacyConsent,
    partnerAgentTermsConsent,
    updateCustomerConsent,
    updatePartnerAgentConsent,
    isUserAuthenticated,
  ]);

  const hasRequestedDuties = useMemo(
    () =>
      (me as me_me_Customer)?.houses?.some(
        house => house.requestedDuties && house.requestedDuties.length > 0,
      ),
    [me],
  );

  const readingTCOrPrivacy = ['/privacy', 'voorwaarden'].includes(pathname);

  const modalTitle = userIsPartnerAgent
    ? 'Algemene voorwaarden & Privacy statement'
    : 'We hebben uw toestemming nodig';

  if (!me) {
    return <></>;
  }

  return (
    <Modal
      mobile={mobile}
      onRequestClose={() => null}
      isOpen={consentModalOpen && !readingTCOrPrivacy}
      title={modalTitle}
    >
      <Flex flexWrap="wrap">
        <Box width={1} textAlign="center">
          <Icon mb={2} icon={ShieldCheck} stroke="green" width="60px" />

          <h4>Welkom {me.firstName}!</h4>
        </Box>
        <Box width={1}>
          {userIsPartnerAgent ? (
            <Color c="grayDark">
              Door je via bovenstaande knop aan te melden ga je akkoord met de{' '}
              <a href="/voorwaarden" target="_blank">
                voorwaarden
              </a>{' '}
              die zijn verbonden aan het gebruik van het partnerportaal Samen en ons{' '}
              <a href="/privacy" target="_blank">
                privacy statement
              </a>
              .
            </Color>
          ) : hasRequestedDuties ? (
            <>
              <p>
                <Book>
                  Fijn dat u door ons geholpen wilt worden bij het verduurzamen van uw woning!
                </Book>
              </p>
              <Small>
                <p>
                  We zien dat er een aanvraag is geplaatst voor uw woning, maar dat wij nog geen
                  akkoord van u persoonlijk hebben ontvangen op onze{' '}
                  <HybridLink to="/voorwaarden" title="Lees onze algemene voorwaarden">
                    algemene voorwaarden
                  </HybridLink>{' '}
                  en{' '}
                  <HybridLink to="/privacy" title="Lees onze privacyverklaring">
                    privacyverklaring
                  </HybridLink>
                  . Dit komt doordat uw aanvraag via een van onze partners is geplaatst. Wij hebben
                  uw toestemming nodig om u van dienst te kunnen zijn.
                </p>
              </Small>
            </>
          ) : (
            <Small>
              <p>
                We zien dat wij nog geen akkoord van u persoonlijk hebben ontvangen op onze{' '}
                <HybridLink to="/voorwaarden" title="Lees onze algemene voorwaarden">
                  algemene voorwaarden
                </HybridLink>{' '}
                en{' '}
                <HybridLink to="/privacy" title="Lees onze privacyverklaring">
                  privacyverklaring
                </HybridLink>
                . Wij hebben uw toestemming nodig om uw account te activeren en u van dienst te
                kunnen zijn bij het verduurzamen van uw woning.
              </p>
            </Small>
          )}

          <Button
            fluid
            label="Prima, ik ben akkoord"
            onClick={handleStoreConsent}
            iconStart={ThumbsUp}
            mr={0}
            mt={2}
          />
          <Smaller>
            <p>
              Mocht u niet akkoord zijn, kunt u{' '}
              <a
                href="#"
                className="link-gray medium"
                onClick={() => setContactModalContent('contactForm')}
              >
                ons een bericht sturen
              </a>{' '}
              met een eventuele reden waarom en het verzoek uw gegevens uit ons systeem te
              verwijderen.
            </p>
          </Smaller>
        </Box>
      </Flex>
    </Modal>
  );
};

export default ConsentModal;
